import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle, Lock, PaperPlaneTilt, Paperclip, Sparkle, Trophy,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { fmtPeriod } from "@/lib/period";
import { SltRespondents } from "@/pages/panchatanthra/SltRespondents";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const TONES = [
  { bar: "bg-terracotta", text: "text-[hsl(20_86%_42%)]", ring: "ring-terracotta", soft: "bg-terracotta/10", wash: "from-terracotta/10" },
  { bar: "bg-teal", text: "text-teal", ring: "ring-teal", soft: "bg-teal/10", wash: "from-teal/10" },
  { bar: "bg-gold", text: "text-goldink", ring: "ring-gold", soft: "bg-gold/12", wash: "from-gold/12" },
  { bar: "bg-plum", text: "text-plumink", ring: "ring-plum", soft: "bg-plum/10", wash: "from-plum/10" },
  { bar: "bg-deepblue", text: "text-deepblue", ring: "ring-deepblue", soft: "bg-deepblue/10", wash: "from-deepblue/10" },
  { bar: "bg-navy", text: "text-navy", ring: "ring-navy", soft: "bg-navy/8", wash: "from-navy/10" },
];

export default function Survey() {
  const { framework } = useParams();
  const [params, setParams] = useSearchParams();
  const scopedElement = params.get("element");
  const period = params.get("period");
  const respondent = params.get("respondent");
  const { tenantId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cursor, setCursor] = useState(0);
  const [stage, setStage] = useState("question"); // question | section-done | review
  const [saving, setSaving] = useState(false);
  const [enter, setEnter] = useState(true);
  const [evidence, setEvidence] = useState([]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/tenants/${tenantId}/surveys/${framework}`,
        { params: { ...(scopedElement ? { element: scopedElement } : {}), ...(period ? { period } : {}),
                    ...(respondent ? { respondent } : {}) } });
      setData(res.data);
    } catch (e) {
      setData({ error: errMsg(e) });
    }
  }, [tenantId, framework, scopedElement, period, respondent]);

  useEffect(() => { load(); }, [load]);

  // Flat walk of every unlocked question, in section order.
  const flat = useMemo(() => {
    if (!data?.sections) return [];
    const out = [];
    data.sections.forEach((s, si) => {
      s.questions.forEach((q, qi) => {
        out.push({ ...q, section: s, sectionIndex: si, indexInSection: qi });
      });
    });
    return out;
  }, [data]);

  // Land on the first unanswered question of the first open section.
  useEffect(() => {
    if (!data?.sections || flat.length === 0) return;
    const firstOpen = data.sections.findIndex((s) => !s.locked && !s.complete);
    const target = flat.findIndex((f) => !data.answers?.[f.code]
      && f.sectionIndex === (firstOpen === -1 ? f.sectionIndex : firstOpen));
    setCursor(target === -1 ? 0 : target);
    if (data.sections.every((s) => s.complete)) setStage("review");
  }, [data?.version_label]); // eslint-disable-line

  const current = flat[cursor];
  const answered = data?.progress?.answered ?? 0;
  const total = data?.progress?.total ?? 0;
  const allComplete = data?.sections?.length > 0 && data.sections.every((s) => s.complete);

  const step = (delta) => {
    setEnter(false);
    setTimeout(() => { setCursor((c) => Math.min(Math.max(c + delta, 0), flat.length - 1)); setEnter(true); }, 120);
  };

  const choose = async (option) => {
    if (!current || saving) return;
    setSaving(true);
    try {
      const res = await api.put(`/tenants/${tenantId}/surveys/${framework}/answers`, {
        question_code: current.code, element_code: current.section.code,
        sub_element_code: current.sub_element_code,
        option_value: option?.value ?? null, option_label: option?.label ?? "Not applicable",
        na: !option, notes: data.answers?.[current.code]?.notes || null,
        section_only: !!scopedElement,
      }, { params: { ...(period ? { period } : {}), ...(respondent ? { respondent } : {}) } });
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const advance = () => {
    const section = data.sections[current.sectionIndex];
    const last = current.indexInSection === current.section.questions.length - 1;
    if (last && section?.complete) {
      setStage(data.sections.every((s) => s.complete) ? "review" : "section-done");
      return;
    }
    step(1);
  };

  const saveNote = async (notes) => {    const a = data.answers?.[current.code];
    await api.put(`/tenants/${tenantId}/surveys/${framework}/answers`, {
      question_code: current.code, element_code: current.section.code,
      sub_element_code: current.sub_element_code,
      option_value: a?.option_value ?? null, option_label: a?.option_label ?? null,
      na: a?.na ?? false, notes,
      section_only: !!scopedElement,
    }, { params: { ...(period ? { period } : {}), ...(respondent ? { respondent } : {}) } })
      .then(({ data: d }) => setData(d)).catch((e) => toast.error(errMsg(e)));
  };

  const evidenceKey = current ? `${framework}:${data?.cycle?.id}:${current.code}` : null;

  const loadEvidence = useCallback(async () => {
    if (!evidenceKey) return;
    try {
      const res = await api.get(`/tenants/${tenantId}/attachments`, {
        params: { scope_type: "evidence", scope_key: evidenceKey, framework },
      });
      setEvidence(res.data);
    } catch { setEvidence([]); }
  }, [evidenceKey, tenantId, framework]);

  useEffect(() => { loadEvidence(); }, [loadEvidence]);

  const uploadEvidence = async (file) => {
    const form = new FormData();
    form.append("scope_type", "evidence");
    form.append("scope_key", evidenceKey);
    form.append("framework", framework);
    form.append("file", file);
    try {
      await api.post(`/tenants/${tenantId}/attachments`, form);
      toast.success(`${file.name} attached`);
      loadEvidence();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async () => {
    try {
      const res = await api.post(`/tenants/${tenantId}/surveys/${framework}/submit`,
        null, { params: { ...(period ? { period } : {}), ...(respondent ? { respondent } : {}),
                          ...(scopedElement ? { element: scopedElement } : {}) } });
      toast.success(`Survey submitted — score ${res.data.overall_score10}/10`);
      setStage("review");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (stage !== "question" || !current) return;
      const n = Number(e.key);
      if (n >= 1 && n <= current.options.length) choose(current.options[n - 1]);
      if (e.key === "ArrowRight") advance();
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  if (!data) return <p className="label-caps animate-pulse" data-testid="survey-loading">Loading survey</p>;
  if (data.error) return <EmptyState testid="survey-error" title="Survey unavailable" message={data.error} />;

  if (data.multi_respondent && !respondent) {
    return (
      <div className="mx-auto max-w-4xl" data-testid="survey-respondent-gate">
        <div className="panel p-7 sm:p-9">
          <p className="label-caps">{data.title} · {data.sections?.[0]?.name || "Element"}</p>
          <SltRespondents
            framework={framework}
            element={scopedElement}
            period={period}
            monthLabel={fmtPeriod(period || data.cycle?.period_label)}
            onPick={(key) => {
              const next = new URLSearchParams(params);
              next.set("respondent", key);
              setParams(next);
            }}
          />
        </div>
      </div>
    );
  }

  const pct = total ? Math.round((answered / total) * 100) : 0;
  const tone = TONES[(current?.sectionIndex ?? 0) % TONES.length];
  const nextSection = data.sections[(current?.sectionIndex ?? 0) + 1];

  return (
    <div className="mx-auto max-w-4xl space-y-8" data-testid={`survey-${framework}`}>
      {/* progress rail */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="label-caps">{data.title} · version {data.version_label}</p>
            <h1 className="mt-1 font-display text-2xl tracking-tight text-navy sm:text-3xl">
              {stage === "review" ? "Review & submit" : current?.section?.name || "Survey"}
            </h1>
            {data.scoped_element && (
              <p className="label-caps mt-1 text-deepblue">Section-only survey</p>
            )}
            {data.respondent && (
              <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-gold/15 px-4 py-1.5 text-[10px] uppercase tracking-[0.14em] text-goldink"
                 data-testid="survey-respondent-badge">
                Answering as {data.respondent.name}
                {period ? ` · ${fmtPeriod(period)}` : ""}
                <button data-testid="survey-change-respondent"
                        onClick={() => {
                          const next = new URLSearchParams(params);
                          next.delete("respondent");
                          setParams(next);
                        }}
                        className="ml-1 underline">Change</button>
              </p>
            )}
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="label-caps">Answered</p>
              <p className="font-mono text-2xl tabular-nums text-navy" data-testid="survey-progress">
                {answered}<span className="text-base text-muted-foreground">/{total}</span>
              </p>
            </div>
            <div className="border-l border-border pl-6 text-right">
              <p className="label-caps">Live score</p>
              <p className="font-mono text-2xl tabular-nums text-navy" data-testid="survey-live-score">
                {data.live_score10 ?? "—"}<span className="text-base text-muted-foreground">/10</span>
              </p>
            </div>
          </div>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-gradient-to-r from-teal via-deepblue to-navy transition-all duration-700"
               style={{ width: `${pct}%` }} />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="survey-stepper">
          {data.sections.map((s, i) => (
            <button
              key={s.code}
              data-testid={`survey-step-${s.code}`}
              disabled={s.locked}
              onClick={() => {
                const idx = flat.findIndex((f) => f.sectionIndex === i);
                if (idx !== -1) { setStage("question"); setCursor(idx); }
              }}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[9px] uppercase tracking-[0.12em] transition-all
                ${current?.sectionIndex === i && stage === "question" ? "border-navy bg-navy text-white"
                  : s.complete ? "border-success/40 bg-success/10 text-success"
                  : s.locked ? "border-border bg-muted text-muted-foreground"
                  : "border-border text-navy hover:border-navy"}`}
            >
              {s.locked ? <Lock size={11} /> : s.complete ? <CheckCircle size={12} weight="fill" />
                : <span className="font-mono">{i + 1}</span>}
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {stage === "question" && current && (
        <div
          key={current.code}
          data-testid={`survey-question-${current.code}`}
          className={`panel relative overflow-hidden transition-all duration-300 ${enter ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
        >
          <div className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b ${tone.wash} to-transparent`} />
          <div className="relative p-7 sm:p-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className={`rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.16em] ${tone.soft} ${tone.text}`}>
                {current.sub_element_name}
              </span>
              <span className="font-mono text-xs tabular-nums text-muted-foreground">
                Question {current.indexInSection + 1} of {current.section.questions.length}
                <span className="mx-2 opacity-40">·</span>
                {current.section.weight_pct}% weight
              </span>
            </div>

            <p className="mt-7 font-display text-2xl leading-snug text-navy sm:text-3xl">
              {current.text}
            </p>

            <div className="mt-8 space-y-3">
              {current.options.map((o, i) => {
                const selected = data.answers?.[current.code]?.option_value === o.value;
                return (
                  <button
                    key={o.value}
                    data-testid={`survey-option-${current.code}-${o.value}`}
                    disabled={saving || data.submitted}
                    onClick={() => choose(o)}
                    className={`group flex w-full items-start gap-4 rounded-xl border p-4 text-left transition-all duration-200
                      ${selected ? `border-transparent ${tone.soft} ring-2 ${tone.ring} shadow-sm`
                        : "border-border hover:-translate-y-0.5 hover:border-navy/40 hover:shadow-md"}
                      disabled:opacity-60`}
                  >
                    <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg font-mono text-sm transition-colors
                      ${selected ? `${tone.bar} text-white` : "bg-muted text-muted-foreground group-hover:bg-navy group-hover:text-white"}`}>
                      {i + 1}
                    </span>
                    <span className="font-body text-base leading-relaxed text-foreground sm:text-[17px]">{o.label}</span>
                    {selected && <CheckCircle size={20} className={`ml-auto shrink-0 ${tone.text}`} weight="fill" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                data-testid={`survey-option-${current.code}-na`}
                disabled={data.submitted}
                onClick={() => choose(null)}
                className={`rounded-full border px-4 py-2 text-[9px] uppercase tracking-[0.14em] transition-colors
                  ${data.answers?.[current.code]?.na ? "border-navy bg-navy text-white"
                    : "border-border text-muted-foreground hover:border-navy/50"}`}
              >
                Not applicable
              </button>
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                Tip: press 1–5 to answer
              </span>
            </div>

            <Textarea
              data-testid={`survey-notes-${current.code}`}
              defaultValue={data.answers?.[current.code]?.notes || ""}
              onBlur={(e) => saveNote(e.target.value)}
              placeholder="Evidence, example or rationale (optional)"
              className="mt-6 text-xs"
            />

            <div className="mt-5 rounded-xl border border-dashed border-border p-4" data-testid={`survey-evidence-${current.code}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Paperclip size={16} className="text-deepblue" />
                  <p className="label-caps">Attach evidence</p>
                  <span className="text-[10px] text-muted-foreground">Excel · PDF · Word · PowerPoint · CSV · image</span>
                </div>
                <label className="cursor-pointer rounded-full border border-navy/25 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-navy transition-colors hover:bg-navy hover:text-white">
                  <input
                    type="file"
                    className="hidden"
                    data-testid={`survey-evidence-input-${current.code}`}
                    onChange={(e) => e.target.files?.[0] && uploadEvidence(e.target.files[0])}
                  />
                  Upload file
                </label>
              </div>
              {evidence.length > 0 && (
                <ul className="mt-4 divide-y divide-border border-t border-border pt-2">
                  {evidence.map((f) => (
                    <li key={f.id} className="flex items-center justify-between gap-3 py-2 text-xs"
                        data-testid={`survey-evidence-file-${f.id}`}>
                      <span className="truncate text-foreground/80">
                        <span className="font-mono text-[10px] text-muted-foreground">v{f.version}</span>{" "}
                        {f.filename}
                      </span>
                      <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                        {f.uploaded_by_email} · {(f.uploaded_at || "").slice(0, 10)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="relative flex items-center justify-between gap-4 border-t border-border bg-muted/40 px-7 py-5 sm:px-10">
            <Button variant="ghost" data-testid="survey-prev" disabled={cursor === 0}
                    onClick={() => step(-1)}
                    className="rounded-full text-[10px] uppercase tracking-[0.16em]">
              <ArrowLeft size={14} className="mr-1.5" /> Previous
            </Button>
            <div className="flex items-center gap-2">
              {data.answers?.[current.code] && (
                <span className="text-[10px] uppercase tracking-[0.14em] text-success">Answer saved</span>
              )}
              <Button data-testid="survey-next"
                      disabled={cursor >= flat.length - 1 && !data.sections[current.sectionIndex]?.complete}
                      onClick={advance}
                      className="rounded-full text-[10px] uppercase tracking-[0.16em]">
                Next <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {stage === "section-done" && (
        <div className="panel overflow-hidden text-center" data-testid="survey-section-complete">
          <div className={`bg-gradient-to-b ${tone.wash} to-transparent px-8 py-12`}>
            <Trophy size={44} className="mx-auto text-gold" weight="fill" />
            <h2 className="mt-5 font-display text-3xl text-navy">
              {data.sections[current.sectionIndex]?.name} complete
            </h2>
            <p className="mt-2 text-sm text-foreground/75">
              Section score {data.element_scores10?.[current.section.code] ?? "—"}/10 ·{" "}
              {nextSection ? `${nextSection.name} is now unlocked.` : "That was the final section."}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {nextSection && (
                <Button data-testid="survey-continue-next"
                        onClick={() => {
                          const idx = flat.findIndex((f) => f.sectionIndex === current.sectionIndex + 1);
                          setCursor(idx === -1 ? cursor : idx);
                          setStage("question");
                        }}
                        className="h-11 rounded-full text-[10px] uppercase tracking-[0.16em]">
                  Continue to {nextSection.name} <ArrowRight size={14} className="ml-1.5" />
                </Button>
              )}
              <Button variant="outline" data-testid="survey-review-answers"
                      onClick={() => setStage("question")}
                      className="h-11 rounded-full text-[10px] uppercase tracking-[0.16em]">
                Review my answers
              </Button>
            </div>
          </div>
        </div>
      )}

      {stage === "review" && (
        <div className="panel overflow-hidden" data-testid="survey-review">
          <div className="bg-gradient-to-b from-gold/12 to-transparent px-8 py-10 text-center">
            <Sparkle size={40} className="mx-auto text-gold" weight="fill" />
            <h2 className="mt-4 font-display text-3xl text-navy">
              {data.submitted ? "Survey submitted" : allComplete ? "All sections complete" : "Almost there"}
            </h2>
            <p className="mt-2 text-sm text-foreground/75">
              {data.submitted ? "Scores are frozen into a snapshot and visible on the framework dashboard."
                : allComplete ? "Review the section scores below, then submit to freeze the score."
                  : `${total - answered} answers still to go.`}
            </p>
          </div>
          <div className="divide-y divide-border">
            {data.sections.map((s, i) => (
              <button
                key={s.code}
                data-testid={`survey-review-section-${s.code}`}
                disabled={s.locked}
                onClick={() => {
                  const idx = flat.findIndex((f) => f.sectionIndex === i);
                  if (idx !== -1) { setCursor(idx); setStage("question"); }
                }}
                className="flex w-full items-center justify-between gap-4 px-8 py-5 text-left transition-colors hover:bg-muted/50"
              >
                <div className="flex items-center gap-4">
                  <span className={`grid h-9 w-9 place-items-center rounded-lg font-mono text-xs
                    ${TONES[i % TONES.length].soft} ${TONES[i % TONES.length].text}`}>{i + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-navy">{s.name}</p>
                    <p className="label-caps mt-0.5">{s.answered}/{s.total} answered · {s.weight_pct}% weight</p>
                  </div>
                </div>
                <p className="font-mono text-lg tabular-nums text-navy">
                  {data.element_scores10?.[s.code] ?? "—"}<span className="text-xs text-muted-foreground">/10</span>
                </p>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border bg-muted/40 px-8 py-6">
            <Button variant="outline" data-testid="survey-back"
                    onClick={() => navigate(framework === "panchatanthra" ? "/panchatanthra" : "/viveka-kartha")}
                    className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
              Back to framework
            </Button>
            <div className="flex gap-2">
              {data.submitted && (
                <Button variant="outline" data-testid="survey-reopen"
                        onClick={async () => {
                          try {
                            await api.post(`/tenants/${tenantId}/surveys/${framework}/reopen`);
                            toast.success("Survey reopened for edits");
                            setStage("question");
                            load();
                          } catch (e) { toast.error(errMsg(e)); }
                        }}
                        className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
                  Reopen survey
                </Button>
              )}
              <Button data-testid="survey-submit" disabled={!allComplete || data.submitted} onClick={submit}
                      className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
                Submit survey <PaperPlaneTilt size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {stage === "question" && (
        <div className="flex justify-center">
          <Button variant="ghost" data-testid="survey-goto-review" onClick={() => setStage("review")}
                  className="rounded-full text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            View all sections & submit
          </Button>
        </div>
      )}
    </div>
  );
}
